import { Edit, InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { MenuMappingMenuResource, getMenuMappingCustomizationOptions } from 'api'
import {
  Availabilities,
  FormattedMenuSectionInfo,
  MenuMappingSectionFormatted,
} from 'api/menuMapping/types'
import { ChilipiperSection, LoadingSkeleton, Select } from 'components'
import { formatMenuSection } from 'features/manage-locations/IndividualMenu/util/formatMenuSection'
import updateAvailabilitiesForMenuSection from 'features/manage-locations/IndividualMenu/util/updateAvailabilitiesForMenuSection'
import validateAvailabilities from 'features/manage-locations/IndividualMenu/util/validateAvailabilities'
import { useGetLocationPartners, useGetMenuMappingSections } from 'hooks'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useRecoilRefresher_UNSTABLE, useRecoilValueLoadable } from 'recoil'
import { menuMappingSectionQuery } from 'state/atoms'
import { FormattedAvailabilities } from 'types'
import { DAYS_OF_WEEK } from 'utils/DateTime'

import {
  SectionContent,
  SectionEditIcon,
  SectionHeader,
  SectionTitle,
  SectionTitleContainer,
  SectionWrapper,
  SelectContainer,
  SelectControl,
  SelectMenu,
  SelectSingleValue,
  SelectValueContainer,
} from '../styles'
import MenuHoursForm from './MenuHoursForm'
import MenuHoursList from './MenuHoursList'

type Option = { label: string; value: string }

type Props = {
  partnerId: string
  isEditing: boolean
  setIsEditing: (isEditing: boolean) => void
}

const MenuHours = ({ partnerId, isEditing, setIsEditing }: Props) => {
  const [menuHours, setMenuHours] = useState<Availabilities[]>([])
  const [menuSectionOptions, setMenuSectionOptions] = useState<Option[]>([])
  const [selectedMenuSectionId, setSelectedMenuSectionId] = useState<string | undefined>()
  const { isLoading, menuMappingMenuId, partner } = useGetLocationPartners(partnerId)
  const { menuMappingSections: menuSections, isLoading: isMenuSectionsLoading } =
    useGetMenuMappingSections(menuMappingMenuId)
  const menuSectionLoadable = useRecoilValueLoadable(menuMappingSectionQuery(selectedMenuSectionId))
  const refreshMenuSection = useRecoilRefresher_UNSTABLE(
    menuMappingSectionQuery(selectedMenuSectionId),
  )
  const [menuSection, setMenuSection] = useState<MenuMappingSectionFormatted>()
  const [combinedMenuSectionData, setCombinedMenuSectionData] = useState<FormattedMenuSectionInfo>()
  const [newAvailabilities, setNewAvailabilities] = useState<FormattedAvailabilities>({})
  const [availabilitiesToDelete, setAvailabilitiesToDelete] = useState<Availabilities['id'][] | []>(
    [],
  )
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false)
  const isPending =
    isLoading ||
    isMenuSectionsLoading ||
    menuSectionLoadable.state === 'loading' ||
    (isEditing && Object.keys(newAvailabilities).length < DAYS_OF_WEEK.length)
  
  const isMenuEmpty = !isPending && menuSectionOptions.length === 0
  const isAllowedToEdit = 
    partner?.attributes?.use_mmr === true &&
    partner?.attributes?.should_sync_menu_hours === false

  useEffect(() => {
    if (menuSections?.length) {
      const options = menuSections.map((section) => ({
        value: section.id,
        label: section.attributes.title,
      }))
      setMenuSectionOptions(options)
    } else {
      setMenuSectionOptions([])
    }
  }, [menuSections])

  const saveMenuHours = async () => {
    if (!partnerId || !selectedMenuSectionId) return
    setSaveButtonDisabled(true)

    const result = await updateAvailabilitiesForMenuSection(
      menuHours,
      newAvailabilities,
      selectedMenuSectionId,
      availabilitiesToDelete,
    )

    if (result) {
      toast.success('Success! Your availability has been updated.', { autoClose: 5000 })
      refreshMenuSection()
      setSaveButtonDisabled(false)
      setIsEditing(false)
    }
  }

  useEffect(() => {
    const formattedAvailabilities: FormattedAvailabilities = {}

    DAYS_OF_WEEK.forEach((day) => {
      const existingAvailability = [...menuHours].find((avail) => avail.dayOfWeek === day)

      if (existingAvailability?.enabled) {
        formattedAvailabilities[existingAvailability.dayOfWeek] =
          existingAvailability.timePeriods.map((timePeriod) => ({
            id: timePeriod.id,
            start_time: timePeriod.attributes.start_time,
            end_time: timePeriod.attributes.end_time,
          }))
      } else {
        formattedAvailabilities[day] = []
      }
    })

    setNewAvailabilities(formattedAvailabilities)
  }, [menuHours])

  useEffect(() => {
    if (!partnerId || !selectedMenuSectionId || !menuMappingMenuId) return
    refreshMenuSection()
  }, [selectedMenuSectionId, menuMappingMenuId])

  useEffect(() => {
    if (menuSectionOptions.length > 0 && !selectedMenuSectionId) {
      setSelectedMenuSectionId(menuSectionOptions[0].value)
    }
  }, [menuSectionOptions, selectedMenuSectionId])

  useEffect(() => {
    if (menuSectionLoadable.state === 'hasValue') {
      setMenuSection(menuSectionLoadable.contents)
    }
  }, [menuSectionLoadable])

  useEffect(() => {
    if (menuSection?.relationships?.menu?.data?.id) {
      const combinedMenuData = formatMenuSection(menuSection)
      setCombinedMenuSectionData(combinedMenuData)

      fetchMenuModifiers(menuSection)
    }
  }, [menuSection])

  const fetchMenuModifiers = async (menuSection: MenuMappingSectionFormatted) => {
    const menuMappingMenuId: MenuMappingMenuResource['id'] =
      menuSection?.relationships?.menu.data.id
    let modifierGroupsData = {}

    try {
      const { formattedGroups: modifierGroups } = await getMenuMappingCustomizationOptions({
        menuMappingMenuId,
        menuMappingSectionId: menuSection.id,
      })

      if (Object.keys(modifierGroups)?.length) {
        modifierGroupsData = modifierGroups
      }
    } catch (err) {
      console.log('failed to fetch item modifiers', err)
    }

    if (menuSection) {
      // format nested menu by combining the menu section data with its modifiers, groups and nested modifiers
      const combinedMenuData = formatMenuSection(menuSection, modifierGroupsData)
      setCombinedMenuSectionData(combinedMenuData)
    }
  }

  useEffect(() => {
    if (combinedMenuSectionData?.attributes?.availabilities) {
      setMenuHours(combinedMenuSectionData.attributes.availabilities)
    }
  }, [combinedMenuSectionData])

  const timePeriodsError = validateAvailabilities(newAvailabilities) === false

  return (
    <SectionWrapper isEditingMode={isEditing}>
      <SectionHeader>
        <SectionTitleContainer>
          <SectionTitle>Menu Hours</SectionTitle>
          <Tooltip
            title='This will update the ordering hours for your menu(s) that appear on online ordering platforms. Please note you may have multiple menus.'
            arrow
          >
            <InfoOutlinedIcon
              style={{
                marginLeft: '8px',
                color: '#777777',
                fontSize: '20px',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </SectionTitleContainer>
        {!isPending && !isEditing && isAllowedToEdit && (
          <Tooltip title='Edit menu hours' arrow>
            <SectionEditIcon onClick={() => isAllowedToEdit && setIsEditing(true)}>
              <Edit />
            </SectionEditIcon>
          </Tooltip>
        )}
      </SectionHeader>
      <SectionContent>
        {isPending ? (
          <LoadingSkeleton isFlex />
        ) : (
          <>
            {!isAllowedToEdit ? (
              <ChilipiperSection
                message='Hours for your menu(s) are managed in your POS system. Make any needed adjustments in your POS.'
                actionBtnText='Learn more about hours settings'
              />
            ) : isMenuEmpty ? (
              <ChilipiperSection
                message='There are currently no menus for this location. If this is unexpected, please reach out to support'
                actionBtnText='Reach out to support'
              />
            ) : null}
        
            <Select
              options={menuSectionOptions}
              name='Menu Name (required)'
              value={
                menuSectionOptions.find((option) => option.value === selectedMenuSectionId) ?? {
                  value: '',
                  label: 'N/A',
                }
              }
              onChange={(option) => {
                setSelectedMenuSectionId(option)
              }}
              styles={{
                container: SelectContainer,
                control: SelectControl,
                menu: SelectMenu,
                valueContainer: SelectValueContainer,
                singleValue: SelectSingleValue,
              }}
              isDisabled={menuSectionOptions.length === 0}
            />

            {isEditing ? (
              <MenuHoursForm
                availabilities={menuHours}
                newAvailabilities={newAvailabilities}
                setNewAvailabilities={setNewAvailabilities}
                availabilitiesToDelete={availabilitiesToDelete}
                setAvailabilitiesToDelete={setAvailabilitiesToDelete}
                timePeriodsError={timePeriodsError}
                saveButtonDisabled={saveButtonDisabled}
                onSave={saveMenuHours}
                onCancel={() => setIsEditing(false)}
              />
            ) : (
              <MenuHoursList availabilities={menuHours} isDisabled={menuSectionOptions.length === 0} />
            )}
          </>
        )}    
      </SectionContent>
    </SectionWrapper>
  )
}

export default MenuHours
